import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { CubeDefinitionDetails } from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';
import { CubeLayerItem, CubeLayerRowItem } from 'types/api/SystemTenant/AROCube/CubeLayerTypes';

export interface CubeDataEntryAttribute {
  id: string;
  name: string;
  displayName: string;
  decimal?: string;
  ruleTypeCode: string;
  coloringRules: string;
  percentage: string;
  isEditable: boolean;
  isCalculated: boolean;
  referenceCode: string;
  lessThanValue: number | null;
  greaterThanValue: number | null;
  lessThanColor: string;
  greaterThanColor: string;
  parent: string;
}

export interface ResponseCubeSyncStatus {
  cubeId: string;
  layerId: string;
  layerName: string;
  memberId: string;
  memberName: string;
  status: string;
}

export interface ResponseCubeBaselineConnection {
  connections: AutoCompleteItem[];
  connectionAttributes: {
    [key: string]: string[];
  };
  cubeLayer: CubeLayerItem;
}

export enum SpecialDataEntryInputType {
  SINGLE_MEMBER_INPUT = 'SINGLE_MEMBER_INPUT',
  MULTI_MEMBER_INPUT = 'MULTI_MEMBER_INPUT',
}

interface SpecialDataEntryValue {
  id: string;
  name: string;
  value: number;
}

export interface ResponseCubeSpecialDataEntry {
  data: {
    [key: string]: SpecialDataEntryValue[];
  };
  memberId: string;
  xmemberId: string;
  attributes: CubeDataEntryAttribute[];
  inputType: SpecialDataEntryInputType;
}

export interface ResponseCubeDataEntry {
  id: string;
  cubeDefinition: CubeDefinitionDetails;
  member: AutoCompleteItem | null;
  attributes: CubeDataEntryAttribute[];
  baseAttributes: CubeDataEntryAttribute[];
  initialAttributes: CubeDataEntryAttribute[];
  data: Data;
  cubeSync: ResponseCubeSyncStatus;
  consolidateView: boolean;
  layer: CubeLayerRowItem;
  todayTimePeriodIndex: number;
}

export interface Data {
  [key: string]: string | number;
}

export interface RequestUpsertCubeDataEntry {
  layerId: string;
  memberId: string;
  xmemberId: string | null;
  cubeId: string;
  data: Data;
}

export interface RequestPullCubeBaselineData {
  cubeId: string;
  targetCubePullId: string;
  layerId: string;
  memberId: string;
}

export interface RequestCubeIngestionRetry {
  layerId: string;
  cubeId: string;
}

export interface RequestSycnCubeData {
  cubeDefinitionId: string;
  layerId: string;
  memberId: string;
  xMemberId: string | null;
}

export interface ResponseCubeIngestionStatus {
  ingestionStatus: string;
  lastUpdated: string;
  cubeDefinitionId: string;
  taskId: string;
  ingestionMessage: string;
}

export interface ResponseCubeActivityStatus {
  id: string;
  cubeDefinitionId: string;
  activityStatus: string;
  activityStatusDescription: string;
  createdDate: string;
  lastModifiedDate: string;
}

export interface ResponseCubeDataEntryChart {
  id: string;
  layer: {
    id: string;
    layerName: string;
    displayName: string;
    layerType: string;
  };
  member: AutoCompleteItem;
  attributes: CubeDataEntryAttribute[];
  chartViewData: string;
}

export type CubeAuditMismatchType =
  | 'VALUE_MISMATCH'
  | 'MISSING_PHYSICAL_ROW'
  | 'INVALID_PHYSICAL_ROW'
  | string;

export interface CubeAuditMismatch {
  type: CubeAuditMismatchType;
  memberId: string | null;
  xMemberId: string | null;
  timestamp: string | null;
  timePeriod: string | null;
  attribute: string | null;
  physicalValue: number | string | null;
  calculatedValue: number | string | null;
}

export interface ResponseCubeDataEntryAudit {
  cubeDefinitionId: string;
  layerId: string;
  tableName: string;
  healthy: boolean;
  tolerance: number;
  physicalRowCount: number;
  calculatedRowCount: number;
  checkedRowCount: number;
  checkedCellCount: number;
  mismatchCount: number;
  missingPhysicalRowCount: number;
  truncated: boolean;
  limit: number;
  mismatches: CubeAuditMismatch[];
}

export interface CubeReferenceAuditMismatch {
  type: CubeAuditMismatchType;
  path: string;
  localValue: number | string | null;
  referenceValue: number | string | null;
}

export interface ResponseCubeDataEntryReferenceAudit {
  healthy: boolean;
  localPath: string;
  referenceUrl: string;
  tolerance: number;
  checkedCellCount: number;
  mismatchCount: number;
  truncated: boolean;
  limit: number;
  mismatches: CubeReferenceAuditMismatch[];
}
